import React, { useState, useEffect } from "react";
import { TextField, Button, Typography, Divider, Switch, FormControlLabel, Tooltip, MenuItem } from "@mui/material";
import { Add, InfoOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useUnit } from "effector-react";
import { Effect } from "effector";
import { BillingPeriod, Fleet, FleetRule } from "../../../../types/fleets.types";
import { WashingType } from "../../../../types/washingTypes.types";
import { Customer } from "../../../../types/customers.types";
import { CustomersStore } from "../../../../stores/CustomersStore/CustomersStore";
import SimpleModal from "../../../../components/SimpleModal/SimpleModal";
import SelectWashType from "../../../Customers/components/SelectWashType/SelectWashType";
import SelectClient from "../SelectClient/SelectClient";
import SettingsRow from "../SettingsRow/SettingsRow";
import BalanceField from "../../../Customers/components/BalanceField/BalanceField";
import { Container, Row } from "./FleetFormModalStyles";

const emptyRule = (): FleetRule => ({
  weekDay: null,
  startHour: null,
  endHour: null,
  discount: 0,
  discountType: "PERCENTAGE",
});

const todayISO = (): string => new Date().toISOString().slice(0, 10);

interface Props {
  fleet?: Fleet;
  effect: Effect<any, any>;
  title: string;
  confirmText: string;
  cancelText: string;
  TriggerComponent: React.ReactElement<any>;
  isEdit?: boolean;
}

const FleetFormModal: React.FC<Props> = ({
  fleet,
  effect,
  title,
  confirmText,
  cancelText,
  TriggerComponent,
  isEdit = false,
}) => {
  const { washingTypes } = useUnit(CustomersStore);

  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState(fleet?.description ?? "");
  const [client, setClient] = useState<Customer>(fleet?.client ?? ({} as Customer));
  const [washingType, setWashingType] = useState<WashingType>(
    fleet?.washingType ?? ({} as WashingType)
  );
  const [rules, setRules] = useState<FleetRule[]>(
    fleet?.rules?.length ? fleet.rules : []
  );
  const [trigger, setTrigger] = useState(!(fleet?.trigger ?? true));
  const [isCreditAllowed, setIsCreditAllowed] = useState(fleet?.isCreditAllowed ?? false);
  const [balance, setBalance] = useState(fleet?.balance ?? 0);
  const [maxWeeklyEnabled, setMaxWeeklyEnabled] = useState(
    fleet?.weeklyLimit !== undefined && fleet.weeklyLimit < 99999
  );
  const [weeklyLimit, setWeeklyLimit] = useState(
    fleet?.weeklyLimit && fleet.weeklyLimit < 99999 ? fleet.weeklyLimit : 1
  );
  const [maxMonthlyEnabled, setMaxMonthlyEnabled] = useState(
    fleet?.monthlyLimit !== undefined && fleet.monthlyLimit < 99999
  );
  const [monthlyLimit, setMonthlyLimit] = useState(
    fleet?.monthlyLimit && fleet.monthlyLimit < 99999 ? fleet.monthlyLimit : 1
  );
  const [billingStartDate, setBillingStartDate] = useState(
    fleet?.billingStartDate || todayISO()
  );
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod | "">(
    fleet?.billingPeriod ?? ""
  );
  const [dueDays, setDueDays] = useState<string>(
    fleet?.dueDays != null ? String(fleet.dueDays) : ""
  );
  const [billingEmail, setBillingEmail] = useState(fleet?.billingEmail ?? "");

  useEffect(() => {
    if (fleet && open) {
      setDescription(fleet.description);
      setClient(fleet.client ?? ({} as Customer));
      const matched = washingTypes.find(
        (wt) => wt.name === fleet.washingType?.name
      );
      setWashingType(matched ?? ({} as WashingType));
      setRules(fleet.rules?.length ? fleet.rules : []);
      setTrigger(!(fleet.trigger ?? true));
      setIsCreditAllowed(fleet.isCreditAllowed ?? false);
      setBalance(fleet.balance ?? 0);
      const wl = fleet.weeklyLimit;
      setMaxWeeklyEnabled(!!wl && wl < 99999);
      setWeeklyLimit(wl && wl < 99999 ? wl : 1);
      const ml = fleet.monthlyLimit;
      setMaxMonthlyEnabled(!!ml && ml < 99999);
      setMonthlyLimit(ml && ml < 99999 ? ml : 1);
      setBillingStartDate(fleet.billingStartDate || todayISO());
      setBillingPeriod(fleet.billingPeriod ?? "");
      setDueDays(fleet.dueDays != null ? String(fleet.dueDays) : "");
      setBillingEmail(fleet.billingEmail ?? "");
    }
  }, [fleet, open, washingTypes]);

  useEffect(() => {
    if (!open) return;

    const unsubscribeDone = effect.doneData.watch(() => {
      toast.success(isEdit ? "Sucesso ao editar frota!" : "Sucesso ao cadastrar frota!");
      if (!isEdit) handleClear();
      setOpen(false);
    });

    const unsubscribeFail = effect.fail.watch(({ error }: any) => {
      if (error?.code === 409) {
        toast.error("Uma frota com estas configurações já existe!");
      } else {
        toast.error(isEdit ? "Erro ao editar frota!" : "Erro ao cadastrar frota!");
      }
    });

    return () => {
      unsubscribeDone();
      unsubscribeFail();
    };
  }, [effect, isEdit, open]);

  const handleClear = () => {
    setDescription("");
    setClient({} as Customer);
    setWashingType({} as WashingType);
    setRules([]);
    setTrigger(false);
    setIsCreditAllowed(false);
    setBalance(0);
    setMaxWeeklyEnabled(false);
    setWeeklyLimit(1);
    setMaxMonthlyEnabled(false);
    setMonthlyLimit(1);
    setBillingStartDate(todayISO());
    setBillingPeriod("");
    setDueDays("");
    setBillingEmail("");
  };

  const handleRuleChange = (index: number, updated: FleetRule) => {
    setRules((prev) => prev.map((r, i) => (i === index ? updated : r)));
  };

  const handleRemoveRule = (index: number) => {
    setRules((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!description || !client?.id || !washingType?.id) {
      toast.error("Preencha todos os campos!");
      return;
    }

    if (!billingStartDate || !billingPeriod || dueDays === "") {
      toast.error("Preencha todos os campos de cobrança!");
      return;
    }

    if (billingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingEmail)) {
      toast.error("Informe um e-mail de cobrança válido!");
      return;
    }

    const normalizedRules = rules.map((r) => ({
      ...r,
      weekDay: r.weekDay || null,
      startHour: r.startHour || null,
      endHour: r.endHour || null,
    }));

    const payload = {
      ...(isEdit && fleet?.id ? { id: fleet.id } : {}),
      description,
      clientId: client.id,
      washingTypeId: washingType.id,
      rules: normalizedRules,
      trigger: !trigger,
      isCreditAllowed,
      balance: isCreditAllowed ? balance : 0,
      weeklyLimit: maxWeeklyEnabled ? weeklyLimit : 99999,
      monthlyLimit: maxMonthlyEnabled ? monthlyLimit : 99999,
      billingStartDate,
      billingPeriod,
      dueDays: Number(dueDays),
      billingEmail,
    };

    effect(payload);
  };

  const modalBody = (
    <Container>
      <TextField
        fullWidth
        label="Descrição"
        variant="outlined"
        required
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <Row>
        <SelectClient client={client} onChange={setClient} />
        <SelectWashType washingType={washingType} onChange={setWashingType} />
      </Row>
      <Divider sx={{ width: "100%" }} />
      <Typography variant="subtitle2" sx={{ alignSelf: "flex-start", fontWeight: 600 }}>
        Cobrança
      </Typography>
      <Row style={{ flexWrap: "wrap" }}>
        <TextField
          label="Data de início"
          variant="outlined"
          type="date"
          required
          slotProps={{ inputLabel: { shrink: true } }}
          value={billingStartDate}
          onChange={(e) => setBillingStartDate(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <TextField
          select
          label="Período de fechamento"
          variant="outlined"
          required
          value={billingPeriod}
          onChange={(e) => setBillingPeriod(e.target.value as BillingPeriod)}
          sx={{ flex: 1, minWidth: 200 }}
        >
          <MenuItem value="WEEKLY">Semanal</MenuItem>
          <MenuItem value="BIWEEKLY">Quinzenal</MenuItem>
          <MenuItem value="MONTHLY">Mensal</MenuItem>
        </TextField>
      </Row>
      <Row style={{ flexWrap: "wrap" }}>
        <TextField
          label="Período de vencimento"
          variant="outlined"
          type="number"
          required
          value={dueDays}
          onChange={(e) => setDueDays(e.target.value)}
          slotProps={{ htmlInput: { min: 1 } }}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <TextField
          label="Email"
          variant="outlined"
          type="email"
          value={billingEmail}
          onChange={(e) => setBillingEmail(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
      </Row>
      <Divider sx={{ width: "100%" }} />
      <Typography variant="subtitle2" sx={{ alignSelf: "flex-start", fontWeight: 600 }}>
        Configurações
      </Typography>
      <Row style={{ alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <FormControlLabel
          control={<Switch checked={trigger} onChange={(e) => setTrigger(e.target.checked)} />}
          label="Acionamento por regra"
        />
        <Tooltip title="Habilite para o acionamento por frota acontecer somente se respeitar as regras configuradas. Desabilite para que, mesmo que as regras não sejam respeitadas, o acionamento por frota aconteça normalmente." placement="top">
          <InfoOutlined fontSize="small" sx={{ color: "text.secondary", cursor: "pointer" }} />
        </Tooltip>
      </Row>
      <Row style={{ alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <FormControlLabel
          control={
            <Switch
              checked={isCreditAllowed}
              onChange={(e) => setIsCreditAllowed(e.target.checked)}
            />
          }
          label="Habilitar crédito"
        />
        {isCreditAllowed && (
          <div style={{ width: 200 }}>
            <BalanceField label="Crédito" value={balance} onChange={setBalance} />
          </div>
        )}
      </Row>
      <Row style={{ alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <FormControlLabel
          control={
            <Switch
              checked={maxWeeklyEnabled}
              onChange={(e) => setMaxWeeklyEnabled(e.target.checked)}
            />
          }
          label="Limite semanal"
        />
        {maxWeeklyEnabled && (
          <TextField
            type="number"
            label="Máx. por semana"
            size="small"
            value={weeklyLimit}
            onChange={(e) => setWeeklyLimit(Number(e.target.value))}
            slotProps={{ htmlInput: { min: 1 } }}
            sx={{ width: 160 }}
          />
        )}
      </Row>
      <Row style={{ alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <FormControlLabel
          control={
            <Switch
              checked={maxMonthlyEnabled}
              onChange={(e) => setMaxMonthlyEnabled(e.target.checked)}
            />
          }
          label="Limite mensal"
        />
        {maxMonthlyEnabled && (
          <TextField
            type="number"
            label="Máx. por mês"
            size="small"
            value={monthlyLimit}
            onChange={(e) => setMonthlyLimit(Number(e.target.value))}
            slotProps={{ htmlInput: { min: 1 } }}
            sx={{ width: 160 }}
          />
        )}
      </Row>
      <Divider sx={{ width: "100%" }} />
      <Typography variant="subtitle2" sx={{ alignSelf: "flex-start", fontWeight: 600 }}>
        Regras
      </Typography>
      {rules.map((r, i) => (
        <SettingsRow
          key={i}
          rule={r}
          onChange={(updated) => handleRuleChange(i, updated)}
          onRemove={() => handleRemoveRule(i)}
          showRemove={true}
        />
      ))}
      <Button
        startIcon={<Add />}
        variant="outlined"
        size="small"
        onClick={() => setRules((prev) => [...prev, emptyRule()])}
        sx={{ alignSelf: "flex-start" }}
      >
        Adicionar regra
      </Button>
    </Container>
  );

  return (
    <SimpleModal
      open={open}
      setOpen={setOpen}
      Component={TriggerComponent}
      onClose={!isEdit ? handleClear : undefined}
      onConfirm={handleSubmit}
      title={title}
      confirmText={confirmText}
      cancelText={cancelText}
      body={modalBody}
      maxWidth="md"
    />
  );
};

export default FleetFormModal;
